<script setup lang="ts">
import type { RouteRecordNormalized } from 'vue-router';

const loading = defineModel<boolean>('loading');
const results = defineModel<boolean>('results');
const props = withDefaults(defineProps<{
    query?: string;
}>(), {
    query: ''
});
const emit = defineEmits<{
    (e: 'close'): void;
}>();

const router = useRouter();
const { $sitemap } = useSitemap();
const { $t } = useLocalization();

const paths = ref<Option[]>([]);
const routes = ref<string[]>([]);
const redirects = ref<Record<string, string>>({});
const filteredPaths = computed(() =>
{
    if (props.query.length > 0)
    {
        return paths.value
            .filter(p =>
                p.text.toLowerCase().indexOf(props.query.toLowerCase()) >= 0
            )
            .slice(0, 5);
    }

    return [];
});

onMounted(async () =>
{
    router.getRoutes().forEach(prepareRoutes);
    preparePaths(await $sitemap.all());
});

const close = () =>
{
    emit('close');
};

const children = (node: any): any[] =>
{
    return node.children.filter((p: any) => p.allowed);
};

const preparePaths = (items: any[], parent: Option = null): void =>
{
    for (const item of items)
    {
        const route = {
            value: item.route,
            text: $t(parent ? `${parent.text} / ${item.name}` : item.name),
            icon: item.icon ? item.icon : parent.icon
        };

        if (route.value && routes.value.includes(route.value))
        {
            route.value = router.resolve({ name: route.value });
            paths.value.push(route);
        }

        preparePaths(children(item), route);
    }
};

const prepareRoutes = (route: RouteRecordNormalized): void =>
{
    const params = route.path.split('/').filter(p => p.startsWith(':') && !p.endsWith('?'));

    if (route.name && params.length === 0)
    {
        routes.value.push(route.name.toString());
    }

    if (route.meta?.model)
    {
        redirects.value[route.meta.model as string] = route.name as string;
    }
};

const highlight = (text: string): string =>
{
    if (props.query.length > 0)
    {
        return text.replace(new RegExp(`(${props.query})`, 'ig'), (match, p1) => `<b>${p1}</b>`);
    }

    return text;
};

watch(filteredPaths, (value) =>
{
    results.value = value.length > 0;
});
</script>

<template>
    <div v-if="!loading || filteredPaths.length > 0" class="list-group mb-2">
        <RouterLink
            v-for="(item, i) in filteredPaths"
            :key="i"
            class="list-group-item list-group-item-action pointer d-flex align-items-center"
            :to="item.value"
            @click="close"
        >
            <i class="fa fa-fw me-3" :class="item.icon" />
            <span v-html="highlight(item.text)" />
        </RouterLink>
    </div>
</template>
